import { NetworkError } from '@pulsify/shared/network-error'
import * as m from '~/paraglide/messages'

interface ApiErrorResponse {
  errorCode: string
  message: string
  fieldErrors?: Record<string, string[]> | null
}

interface AxiosLikeError {
  response?: {
    status: number
    data: unknown
  }
  request?: unknown
}

/**
 * Custom error class for API errors with error codes
 */
export class ApiError extends Error {
  public readonly errorCode: string
  public readonly statusCode: number

  constructor(errorCode: string, message: string, statusCode: number) {
    super(message)
    this.name = 'ApiError'
    this.errorCode = errorCode
    this.statusCode = statusCode
  }
}

function getTranslatedErrorMessage(errorCode: string): string | undefined {
  const errorMessageMap: Record<string, () => string> = {
    AccountAlreadyExists: m.accountWithEmailExists,
    InvalidCredentials: m.invalidEmailOrPassword,
    AccountNotFound: m.accountNotFoundError,
    AccountDisabled: m.accountDisabledError,
    AccountNotVerified: m.pleaseVerifyEmailFirst,
    EmailNotVerified: m.pleaseVerifyEmailFirst,
    EmailAlreadyVerified: m.emailAlreadyVerifiedError,
    InvalidVerificationCode: m.invalidOrExpiredVerificationCode,
    VerificationCodeExpired: m.verificationCodeExpiredError,
    TooManyVerificationAttempts: m.tooManyVerificationAttempts,
    PasswordResetRequired: m.passwordResetRequiredError,
    WeakPassword: m.weakPasswordError,
    InvalidPassword: m.invalidEmailOrPassword,
    UserNotFound: m.accountNotFoundError,
    InvalidEmail: m.invalidEmailOrPassword,
    EmailAlreadyInUse: m.accountWithEmailExists,
    BusinessNotFound: m.businessNotFoundError,
    BusinessAlreadyExists: m.businessAlreadyExistsError,
    UserAlreadyHasBusiness: m.userAlreadyHasBusinessError,
    RegistrationEmailFailed: m.registrationEmailFailedError,
    PartnerCodeTaken: m.partnerCodeTakenError,
    BusinessProfileIncomplete: m.businessProfileIncompleteError,
    InvalidBusinessData: m.invalidBusinessDataError,
    EventNotFound: m.eventNotFoundError,
    RegistrationNotFound: m.registrationNotFoundError,
    EventAlreadyPublished: m.eventAlreadyPublishedError,
    ChannelHasUpcomingEvents: m.hasUpcomingEventsError,
    ListingHasUpcomingEvents: m.hasUpcomingEventsError,
    EventAlreadyCancelled: m.eventAlreadyCancelledError,
    EventInPast: m.eventInPastError,
    InvalidEventData: m.invalidEventDataError,
    ListingNotFound: m.listingNotFoundError,
    MenuNotFound: m.menuNotFoundError,
    ListingActionDailyLimit: m.actionDailyLimitError,
    ListingActionInvalidEnd: m.actionInvalidEndError,
    ListingActionNotFound: m.actionNotFoundError,
    ListingAlreadyExists: m.listingAlreadyExistsError,
    InvalidListingData: m.invalidListingDataError,
    SubscriptionLimitReached: m.subscriptionLimitReached,
    InvalidPriceId: m.invalidPriceIdError,
    InvalidUpgrade: m.invalidUpgradeError,
    AlreadySubscribed: m.alreadySubscribedError,
    CheckoutFailed: m.checkoutFailedError,
    PaymentDeclined: m.paymentDeclinedError,
    PaymentProviderUnavailable: m.paymentProviderUnavailableError,
    PaymentProviderError: m.paymentProviderError,
    InvalidVatId: m.invalidVatIdError,
    RedirectNotAllowed: m.redirectNotAllowedError,
    TrialAlreadyActive: m.trialAlreadyActiveError,
    InvalidTrialRequest: m.invalidTrialRequestError,
    Unauthorized: m.unauthorizedError,
    Forbidden: m.forbiddenError,
    InsufficientPermissions: m.insufficientPermissionsError,
    RateLimitExceeded: m.rateLimitExceededError,
    InternalServerError: m.internalServerErrorMessage,
    ServiceUnavailable: m.serviceUnavailableError,
    ValidationError: m.validationErrorMessage,
    ValidationFailed: m.validationErrorMessage,
    MissingRequiredField: m.missingRequiredFieldError,
    InvalidFormat: m.invalidFormatError,
    FileTooLarge: m.fileTooLargeError,
    InvalidFileType: m.invalidFileTypeError,
    UploadFailed: m.uploadFailedError,
    GenerationFailed: m.aiGenerationFailedError,
    ImageGenerationFailed: m.aiGenerationFailedError,
    EventPageUnreadable: m.eventPageUnreadableError,
    CoordinatesNotFoundForAddress: m.eventAddressNotFound,
    EventDraftEmpty: m.eventDraftEmptyError,
    PlaceWebsiteUnreadable: m.placeWebsiteUnreadableError,
    PlaceWebsiteNoPlace: m.placeWebsiteNoPlaceError,
    PlaceWebsiteDailyLimit: m.placeWebsiteDailyLimitError,
    ImageInvalidFormat: m.imageUploadInvalidType,
    ImageTooLarge: m.fileTooLargeError,
    OrganizerLinkCodeInvalid: m.profileLinkCodeInvalidError,
    OrganizerLinkNotFound: m.profileLinkNotFoundError,
    LiveRoomRequestNotFound: m.requestNotFoundError,
    LiveRoomNoPlacesLeft: m.requestNoPlacesLeft,
    TicketPriceTooLow: m.ticketPriceTooLowError,
    TicketQuantityBelowSold: m.ticketQuantityBelowSoldError,
    TicketTypeNotFound: m.ticketTypeNotFoundError,
    PaymentsNotSetUp: m.ticketPaymentsNotSetUpError,
    FirstSalesCapReached: m.ticketFirstSalesCapError,
    TicketRedirectNotAllowed: m.ticketRedirectNotAllowedError,
    OrderNotFound: m.ticketOrderNotFoundError,
    OrderNotRefundable: m.ticketOrderNotRefundableError,
    OrderAlreadyPaidOut: m.ticketOrderAlreadyPaidOutError,
    TicketNotFound: m.ticketNotFoundError,
    TicketNotVoidable: m.ticketNotVoidableError,
    TicketSalesEnded: m.ticketSalesEndedError,
    EventAlreadyPaidOut: m.eventAlreadyPaidOutError,
    InvoiceNotAvailable: m.ticketInvoiceNotAvailableError,
    DocumentLinkInvalid: m.ticketDocumentLinkInvalidError,
  }

  const messageFn = errorMessageMap[errorCode]
  return messageFn ? messageFn() : undefined
}

/**
 * Get a user-friendly error message for an error code
 */
export function getErrorMessage(errorCode: string, fallbackMessage?: string): string {
  return getTranslatedErrorMessage(errorCode) || fallbackMessage || m.unexpectedErrorOccurred()
}

function isApiErrorResponse(data: unknown): data is ApiErrorResponse {
  return (
    typeof data === 'object' &&
    data !== null &&
    'errorCode' in data &&
    typeof (data as ApiErrorResponse).errorCode === 'string'
  )
}

function getFieldErrorsMessage(fieldErrors: Record<string, string[]>): string | undefined {
  const entries = Object.entries(fieldErrors)
  if (entries.length === 0) return undefined

  const [, errors] = entries[0]
  if (errors && errors.length > 0) {
    return errors[0]
  }
  return undefined
}

function hasMessageField(data: unknown): data is { message: string } {
  return (
    typeof data === 'object' &&
    data !== null &&
    'message' in data &&
    typeof (data as { message: string }).message === 'string'
  )
}

function isApiError(error: unknown): error is ApiError {
  return (
    error instanceof ApiError ||
    (error instanceof Error &&
      error.name === 'ApiError' &&
      'errorCode' in error &&
      'statusCode' in error)
  )
}

/**
 * Extract error information from an Axios error
 */
export function extractApiError(error: unknown): ApiError {
  if (isApiError(error)) {
    return error
  }

  if (error instanceof NetworkError) {
    const code = error.code ?? 'UnknownError'
    const userMessage = getErrorMessage(code, error.message)
    return new ApiError(code, userMessage, error.statusCode ?? 0)
  }

  const axiosError = error as AxiosLikeError

  if (axiosError.response?.data) {
    const data = axiosError.response.data

    if (isApiErrorResponse(data)) {
      const { errorCode, message, fieldErrors } = data

      let userMessage: string
      if (fieldErrors && Object.keys(fieldErrors).length > 0) {
        const fieldErrorMessage = getFieldErrorsMessage(fieldErrors)
        userMessage = fieldErrorMessage || getErrorMessage(errorCode, message)
      } else {
        userMessage = getErrorMessage(errorCode, message)
      }

      return new ApiError(errorCode, userMessage, axiosError.response.status)
    }

    if (hasMessageField(data)) {
      return new ApiError('UnknownError', data.message, axiosError.response.status)
    }

    if (typeof data === 'string' && data.length > 0) {
      return new ApiError('UnknownError', data, axiosError.response.status)
    }
  }

  if (axiosError.response) {
    console.warn('[ApiError] Unhandled error response format:', {
      status: axiosError.response.status,
      data: axiosError.response.data,
      dataType: typeof axiosError.response.data,
    })
    return new ApiError('UnknownError', m.unexpectedErrorOccurred(), axiosError.response.status)
  }

  if (axiosError.request) {
    return new ApiError('NetworkError', m.noServerConnection(), 0)
  }

  return new ApiError(
    'UnknownError',
    error instanceof Error ? error.message : m.unexpectedErrorOccurred(),
    0,
  )
}

/**
 * Get error message from an error, handling both ApiError and regular errors
 */
export function getErrorMessageFromError(error: unknown): string {
  if (isApiError(error)) {
    return error.message
  }

  const apiError = extractApiError(error)
  return apiError.message
}
